import {
    fetchRoute,
    initFieldValidation,
    Link,
    ResourceEdit,
    TextField,
    TFieldValidation,
    Typography,
    validateGenericInput,
    validateUrl
} from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { ChangeEvent, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { ERoutes } from "../../../../utils/url"
import { injectRecaptchaOptions } from "../../utils"
import { OptInPolicy } from "../optInPolicy/OpinPolicy"
import { TFromCommonResourceEdit } from "./types"

export function FromCommonResourceEdit({ form, content, saveForm, saveContent }: TFromCommonResourceEdit) {
    const [fieldStates, setFieldStates] = useState<Record<string, TFieldValidation>>({
        ...initFieldValidation("post_redirect_url", { value: form?.post_redirect_url }),
        ...initFieldValidation("double_opt_in_redirect_url", { value: form?.double_opt_in_redirect_url })
    })
    const [recaptchaFieldStates, setRecaptchaFieldStates] = useState<Record<string, TFieldValidation>>({
        ...initFieldValidation("secret", { value: form?.recaptcha.secret }),
        ...initFieldValidation("site_key", { value: form?.recaptcha.site_key }),
        ...initFieldValidation("domain", { value: form?.recaptcha.domain })
    })
    const { t } = useTranslation()

    function handleOptInPolicyChange(event: ChangeEvent<HTMLInputElement>, value: string) {
        saveForm({ double_opt_in: value === "dOptIn" })
    }

    function handleRedirectionChangeTimeout(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { name, value } = event.target

        const valueStatus = validateUrl(value)

        setFieldStates({
            ...fieldStates,
            [name]: valueStatus
        })

        if (!valueStatus.error) {
            saveForm({
                [name]: value
            })
        }
    }

    function handleRecaptchaChangeTimeout(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { name, value } = event.target

        let valueStatus = validateGenericInput(value)
        if (name === "domain" && value !== "") {
            const isValid = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(value)
            valueStatus = {
                value: value,
                error: !isValid,
                message: !isValid ? "input-validation-invalid-domain" : ""
            }
        }


        const recaptchaUpdatedState: any = { ...recaptchaFieldStates, [name]: valueStatus }
        setRecaptchaFieldStates(recaptchaUpdatedState)

        const recaptcha: any = {
            secret: recaptchaUpdatedState.secret.value,
            site_key: recaptchaUpdatedState.site_key.value,
            //domain: recaptchaUpdatedState.domain.value,
            [name]: value
        }

        if (name === "secret" && value.includes("***")) {
            delete recaptcha.secret
        }
        //&& recaptchaUpdatedState.domain.value
        if (!valueStatus.error && recaptchaUpdatedState.secret.value && recaptchaUpdatedState.site_key.value) {

            const contentToUpdate = content || form.content

            if (saveContent) {
                saveForm({
                    "recaptcha": recaptcha
                })
                saveContent({
                    dataToUpdate: {
                        content: {
                            ...contentToUpdate,
                            json: injectRecaptchaOptions({
                                content: contentToUpdate.json,
                                recaptchaSiteKey: recaptchaUpdatedState.site_key.value
                            })
                        }
                    }
                })
            } else {
                saveForm({
                    "recaptcha": recaptcha,
                    content: {
                        ...contentToUpdate,
                        json: injectRecaptchaOptions({
                            content: contentToUpdate.json,
                            recaptchaSiteKey: recaptchaUpdatedState.site_key.value
                        })
                    }
                })
            }

        }
    }

    const isDefaultPostUrl = form?.post_redirect_url === "" || form?.post_redirect_url?.includes(fetchRoute({ route: ERoutes.listSubscription }))
    const isDefaultDoptInUrl = form?.double_opt_in_redirect_url === "" || form?.double_opt_in_redirect_url?.includes(fetchRoute({ route: ERoutes.listConfirmation }))

    const isRecaptchaValid = form.recaptcha.site_key &&
        form.recaptcha.secret &&
        //form.recaptcha.domain &&
        !recaptchaFieldStates.secret.error &&
        !recaptchaFieldStates.site_key.error
    //!recaptchaFieldStates.domain.error

    return <>
        <ResourceEdit
            editable
            status="valid"
            title={t("general-opt-in-settings")}
            desc={t("form-opt-in-desc")}
            support={<Typography variant="body1"><Trans i18nKey={form.double_opt_in ? "form-opt-in-support-enabled" : "form-opt-in-support-disabled"} /></Typography>}
            editableContent={<OptInPolicy initIsDOptIn={form.double_opt_in} onChange={handleOptInPolicyChange} />}
        />

        <ResourceEdit
            editable
            status={
                fieldStates.post_redirect_url.error || fieldStates.double_opt_in_redirect_url.error ? "error" : "valid"
            }
            title={t("general-redirection_other")}
            desc={t("forms-details-redirections-desc")}
            support={<Stack gap={2}>
                <Typography variant="body1">{t("form-post-redirect-url-desc", { url: isDefaultPostUrl ? t("general-default") : form.post_redirect_url })}</Typography>
                <Typography variant="body1">{t("form-dOpt-in-redirect-url-desc", { url: isDefaultDoptInUrl ? t("general-default") : form.double_opt_in_redirect_url })}</Typography>
            </Stack>}
            editableContent={<Stack gap={6}>
                <TextField
                    name="post_redirect_url"
                    label={t("form-post-redirect-url-label")}
                    placeholder={t("general-url-placeholder")}
                    defaultValue={isDefaultPostUrl ? "" : form.post_redirect_url}
                    timeout={{
                        callback: handleRedirectionChangeTimeout
                    }}
                    helperText={fieldStates.post_redirect_url.error && t(fieldStates.post_redirect_url.message || "")}
                    error={fieldStates.post_redirect_url.error}
                />
                <TextField
                    name="double_opt_in_redirect_url"
                    label={t("form-dOpt-in-redirect-url-label")}
                    placeholder={t("general-url-placeholder")}
                    defaultValue={isDefaultDoptInUrl ? "" : form.double_opt_in_redirect_url}
                    timeout={{
                        callback: handleRedirectionChangeTimeout
                    }}
                    helperText={fieldStates.double_opt_in_redirect_url.error && t(fieldStates.double_opt_in_redirect_url.message || "")}
                    error={fieldStates.double_opt_in_redirect_url.error}
                />
            </Stack>}
        />


        <ResourceEdit
            editable
            status={isRecaptchaValid ? "valid" : "empty"}
            title={t("general-recaptcha")}
            desc={<Link href="https://developers.google.com/recaptcha" target="_blank">{t("forms-details-recaptcha-desc")}</Link>}
            support={<Stack gap={2}>
                <Typography variant="body1"><Trans i18nKey={form.recaptcha.secret ? "forms-details-recaptcha-support-active" : "forms-details-recaptcha-support-inactive"} /></Typography>
            </Stack>}
            editableContent={<Stack gap={6} >
                <TextField
                    name="secret"
                    label={t("general-secret")}
                    placeholder={t("general-secret-placeholder")}
                    defaultValue={form.recaptcha.secret}
                    timeout={{
                        callback: handleRecaptchaChangeTimeout
                    }}
                />
                <TextField
                    name="site_key"
                    label={t("general-site-key")}
                    placeholder={t("general-site-key-placeholder")}
                    defaultValue={form.recaptcha.site_key}
                    timeout={{
                        callback: handleRecaptchaChangeTimeout
                    }}
                />
            </Stack>}
        />
    </>
}